/* Crud list */
import { useEffect, useState, useCallback } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import {
    useGridApiRef,
    GRID_STRING_COL_DEF
} from '@mui/x-data-grid';
import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import { TextField, Checkbox } from '@mui/material';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import InsertIcon from '@mui/icons-material/AddBoxOutlined';
import InsertOnFormIcon from '@mui/icons-material/PlaylistAddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import { t } from './../i18n';
import Image from 'react-bootstrap/Image';
import GridDateEditCell from '../Components/GridDateEditCell';
import { callApi, getPropValue, ErrorAlert, Round, getCultureInfo } from './utils';
import CrudForm from './CrudForm';
import GridDashboard from './GridDashboard';
import FieldSelector from './FieldSelector';
import ListQueryWizard from './ListQueryWizard';
import { parse, format } from 'date-fns';
import localeMap from './locales';
import './CrudList.css';
import useApp from '../AppContext';
import DynamicDatePicker from '../Components/DynamicDatePicker';
import * as XLSX from 'xlsx'; // npm i xlsx --legacy-peer-deps


const editGrid = false;
function GridImageCell(props) {
    if (props.value == null) {
        return null;
    }
    return <img className="grid-img-col" src={`/images/${props.value}`} alt={props.value} width={props.colDef.computedWidth} />
}

const imageColumnType = {
    ...GRID_STRING_COL_DEF,
    type: 'custom',
    resizable: false,
    filterable: false,
    sortable: false,
    editable: false,
    groupable: false,
    display: 'flex',
    renderCell: (params) => <GridImageCell {...params} />,
};

function CrudList(props) {
    const app = useApp();
    const [layout, setLayout] = useState(null);
    const [error, setError] = useState(null);
    const [serverUrl] = useState(app.serverUrl);
    const [gridTop] = useState(app.gridTop);
    const [prmValues, setPrmValues] = useState({});
    const [prmControlValues, setPrmControlValues] = useState({});
    const [editFormActive, setEditFormActive] = useState(false);
    const [type, setType] = useState("Grid");
    const [actions, setActions] = useState(null);
    const [activeRowIndex, setActiveRowIndex] = useState(-1);
    const [isDashboardActive, setIsDashboardActive] = useState(false);
    const [actionLayout, setActionLayout] = useState(null);
    const [toolbarActions, setToolbarActions] = useState([]);
    const gridApiRef = useGridApiRef();
    const [language] = useState(getCultureInfo(app.sessionToken?.Language));
    
    const IconStyles = {
        fontSize: '20px',
        color: app.theme === 'dark' ? 'var(--text-secondary)' : '#353839'
    };
    useEffect(() => {
        if (props.prmValues) {
            setPrmValues(props.prmValues);
        }
    }, [props.prmValues]);

    const rowSelectedCallBack = useCallback(async function rowSelected(row) {
        if (!layout) return;
        if (props.rowSelected) {
            props.rowSelected(layout.layoutId, row, props);
        }
    }, [layout, props]);

    /*
        useEffect(() => {
            if (props.onDataContextChanged) {
                props.onDataContextChanged(props.dataContex);
            }
        }, [props.dataContex]);
    */
    const { onDataContextChanged } = props;

    useEffect(() => {
        if (onDataContextChanged) {
            onDataContextChanged(props.dataContex);
        }
    }, [onDataContextChanged, props.dataContex]);

    useEffect(() => {
        async function getLayout() {
            try {
                if (!props.dataEngine) return;
                let layout = null;
                if (props.layout) {
                    layout = props.layout;
                } else {
                    if (!props.layoutUrl || !serverUrl) return;
                    let url = `${serverUrl}/${props.layoutUrl}`;
                    if (props.instanceName && url.toLowerCase().indexOf('?instancename=') === -1) url += `?instanceName=${props.instanceName}`;
                    layout = await callApi('GET', url, props.userToken);
                }
                await props.dataEngine.initObjectMeta(layout.queryObject, layout.instanceName, layout, null);
                setLayout(layout);
                setActions(layout.actions);
                setActiveRowIndex(-1);
                // console.log(Date.now() + " Load layout for " + layout.queryObject);
            } catch (err) {
                setError(err.message);
            }
        };
        getLayout();
    }, [props.dataEngine, props.instanceName, props.layout, props.layoutUrl, props.userToken, serverUrl]);

    useEffect(() => {
        async function loadData() {
            try {
                if (!layout) return;
                if (!props.dataEngine) return;
                setError(null);
                if (props.syncParameters && !props.parentSelectedRow) {
                    await props.dataEngine.clearData(layout.queryObject);
                    return;
                }
                let prmDefs = [...layout.queryParameters];
                if (props.syncParameters && props.parentSelectedRow) {
                    props.syncParameters.forEach(sp => {
                        prmDefs.push({ "parameterName": sp.parameterField });
                        prmValues[sp.parameterField] = props.parentSelectedRow[sp.masterField];
                    });
                }
                let defVars = {
                    "instanceName": layout.instanceName,
                    "filterName": "default",
                    "top": gridTop
                }
                // console.log(Date.now() + " Load data for " + layout.queryObject);
                const rowSelected = props.rowSelected
                await props.dataEngine.loadData(
                    layout.queryObject, layout.columns,
                    [layout.primaryKey, layout.additionalColumns, props.childrenOfList ? props.childrenOfList.colsForChildSync : []],
                    prmDefs, prmValues, defVars, false, (data) => {
                        const selectedRow = (data && data.length) ? data[0] : null;
                        if (rowSelected) rowSelected(layout.layoutId, selectedRow);
                    }
                );
            } catch (err) {
                setError(err.message);
            }
        }
        loadData();
    }, [gridTop, layout, prmValues, props.childrenOfList, props.dataEngine, props.parentSelectedRow, props.syncParameters, props.rowSelected]);

    async function selectedFieldsChanged() {
        try {
            setLayout(await callApi('GET', `${serverUrl}/${props.layoutUrl}`, props.userToken));
        } catch (err) {
            setError(err.message);
        }
    }

    function prmValueChanged(e, prm) {
        let val = e.target.value;
        if (prm.type === "Date" && val) {
            try {
                const date = new Date(val);
                if (!isNaN(date.getTime())) {
                    val = format(date, 'dd/MM/yyyy');
                }
            } catch (err) {
                console.error('Date parsing error:', err);
            }
        }
        setPrmControlValues(prev => ({ ...prev, [prm.parameterName]: val }));
    }

    async function refreshClicked() {
        setPrmValues({ ...prmControlValues });
    }

    function getSelectedRowIndex() {
        if (!gridApiRef.current.getSelectedRows) return -1;
        let selectedRows = gridApiRef.current.getSelectedRows();
        const rowIndex = selectedRows.keys().next().value;
        if (rowIndex === undefined) return -1;
        else return rowIndex;
    }

    function getSelectedRows() {
        if (!gridApiRef.current.getSelectedRows) return -1;
        let selectedRows = gridApiRef.current.getSelectedRows();
        let retVal = [];
        const data = props.dataContext.getLoadedData(layout.queryObject);
        for (const [key,] of selectedRows) {
            retVal.push(data[key]);
        }
        return retVal;
    }
    // Παρόμοια λειτουργία με την αυθεντική, αλλά η ενεργοποίηση της φόρμας γίνεται με καθυστέρηση μέσω setTimeout,
    // ώστε να διασφαλιστεί ότι έχει ολοκληρωθεί η ενέργεια εισαγωγής ή επεξεργασίας πριν εμφανιστεί η φόρμα.
    /*
     async function activateEditForm(i) {
         if (i === -1) {
             await props.dataContext.insertNewRow(layout.queryObject, false, props.parentSelectedRow);
             setTimeout(() => {
                 setEditFormActive(true);
             }, 50);

             return;
         }
         let rowIndex = i;
         if (i == null && gridApiRef && gridApiRef.current) {
             let selectedRows = gridApiRef.current.getSelectedRows();
             rowIndex = selectedRows.keys().next().value;
             if (rowIndex === undefined) return;
         }
         const data = props.dataContext.getLoadedData(layout.queryObject);
         await props.dataContext.beginEdit(layout.queryObject, data[rowIndex]);
         setTimeout(() => {
             setEditFormActive(true);
         }, 50);
     } 
     */
    // Παρόμοια λειτουργία με την προηγούμενη, αλλά η ενεργοποίηση της φόρμας γίνεται με requestAnimationFrame,
    //γινεται αυτοματοποιημενα και δεν απαιτει καθυστερηση με setTimeout.
    async function activateEditForm(i) {
        const waitNextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

        if (i === -1) {
            await props.dataContext.insertNewRow(layout.queryObject, false, props.parentSelectedRow);
            await waitNextFrame();
            setEditFormActive(true);
            return;
        }

        let rowIndex = i;
        if (i == null && gridApiRef?.current) {
            const selectedRows = gridApiRef.current.getSelectedRows();
            rowIndex = selectedRows.keys().next().value;
            if (rowIndex === undefined) return;
        }

        const data = props.dataContext.getLoadedData(layout.queryObject);
        await props.dataContext.beginEdit(layout.queryObject, data[rowIndex]);
        await waitNextFrame();
        setEditFormActive(true);
    }

    function createPrmControls() {
        if (props.prmValues || !layout || !layout.queryParameters) return null;
        return layout.queryParameters.map((prm, i) => {
            let placeHolder = prm.caption;
            // console.log(`Rendering control for parameter ${prm.parameterName}:`, {
            //     type: prm.type,
            //     value: prmControlValues[prm.parameterName],
            //     caption: placeHolder
            // });
            if (prm.type === "Date") {
                return (
                    <div key={`ctl${i}`} onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            refreshClicked();
                        }
                    }}>
                        <div className="prm-date-picker">
                            <DynamicDatePicker
                                value={prmControlValues[prm.parameterName] || ''}
                                caption={placeHolder}
                                variant="outlined"
                                size="small"
                                pickerType="Date"
                                dateFormat="dd/MM/yyyy"
                                onChange={(selectedDate) => {
                                    let value = null;
                                    if (selectedDate) {
                                        value = format(new Date(selectedDate), "yyyy-MM-dd");
                                    }
                                    prmValueChanged({ target: { value } }, prm);
                                }}
                            />
                        </div>
                    </div>
                );
            } else if (prm.type === "Number") {
                return (
                    <TextField
                        key={`ctl${i}`}
                        id={`prm${i}`}
                        type="number"
                        label={placeHolder}
                        variant="outlined"
                        size="small"
                        value={prmControlValues[prm.parameterName] || ''}
                        onChange={(e) => prmValueChanged(e, prm)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                refreshClicked();
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fff',
                                '&.Mui-focused fieldset': {
                                    borderColor: 'var(--color-main-background)',
                                }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: 'var(--color-main-background)'
                            }
                        }}
                    />
                );
            } else {
                return (
                    <TextField
                        key={`ctl${i}`}
                        id={`prm${i}`}
                        label={placeHolder}
                        variant="outlined"
                        size="small"
                        value={prmControlValues[prm.parameterName] || ''}
                        onChange={(e) => prmValueChanged(e, prm)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                refreshClicked();
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fff',
                                '&.Mui-focused fieldset': {
                                    borderColor: 'var(--color-main-background)'
                                }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: 'var(--color-main-background)'
                            }
                        }}
                    />
                );
            }
        });
    }

    function changeListType() {
        if (type === "Table") {
            setType("Grid");
        } else {
            setType("Table");
        }
    }

    function excelExport() {
        const data = props.dataContext.getLoadedData(layout.queryObject);
        if (!data) return;

        const expData = data.map(ro => {
            let obj = {};
            layout.columns.forEach(c => {
                obj[c.columnName.replaceAll(".", "_")] = getPropValue(ro, c.columnName);
                if (c.format === "d") {
                    obj[c.columnName.replaceAll(".", "_")] = new Date(obj[c.columnName.replaceAll(".", "_")]);
                }
                else if (c.format === "t") {
                    let d = new Date(getPropValue(ro, c.columnName));
                    obj[c.columnName.replaceAll(".", "_")] = d.getHours().toString().padStart(2, '0') + ":" + d.getMinutes().toString().padStart(2, '0');
                } else if (c.controlType === 'DropDownEnumList') {
                    const option = c.controlOptions.find(enm => enm.value === obj[c.columnName.replaceAll(".", "_")]);
                    if (option) obj[c.columnName.replaceAll(".", "_")] = option.text;
                }
            });
            return obj;
        });
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const dateStr = `${dd}-${mm}-${yyyy}`;

        const filename = `${layout.caption} ${dateStr}.xlsx`;
        const workBook = XLSX.utils.book_new();
        const workSheet = XLSX.utils.json_to_sheet(expData);
        XLSX.utils.book_append_sheet(workBook, workSheet, layout.caption);
        XLSX.writeFile(workBook, filename);
    }

    async function actionClicked(e, action) {
        try {
            if (e.ctrlKey) {
                setToolbarActions(prev => {
                    prev = [...prev];
                    if (prev.find(a => a === action)) {
                        prev.splice(prev.indexOf(action), 1);
                    } else {
                        prev.push(action);
                    }
                    return prev;
                });
                return;
            }
            setError(null);
            let rowindex = getSelectedRowIndex();
            if (rowindex === -1) return;
            if (!props.dataEngine) return;
            if (action.requestFormUrl) {
                actionForms(action);
            }
            else {
                actionFormOk(action, {})
            }

        }
        catch (err) {
            setError(err.message);
        }
    }

    async function actionForms(action) {
        let url = `${serverUrl}/${action.requestFormUrl}`;
        if (props.instanceName) url += `?instanceName=${props.instanceName}`;
        let actlayout = await callApi('GET', url, props.userToken);
        await props.dataEngine.clearData(actlayout.queryObject);
        setActionLayout(actlayout);
        await props.dataEngine.initObjectMeta(actlayout.queryObject, actlayout.instanceName, null, actlayout);
        let tmpActions = [...actions];
        tmpActions.find(a => a === action).requestFormIsOpen = true;
        setActions(tmpActions);
    }


    function actionFormCancelled(action) {
        let tmpActions = [...actions];
        tmpActions.find(a => a === action).requestFormIsOpen = false;
        setActions(tmpActions);
    }

    async function actionFormOk(action, obj) {
        try {
            setError(null);
            let selectedRows = getSelectedRows();
            const requestBody = {
                obj: obj,
                action: action,
                data: selectedRows
            };
            let url = `${serverUrl}/api/action?objectName=${layout.queryObject}&instanceName=${layout.instanceName}`;
            //var response = await callApi('POST', url, props.userToken, requestBody);
            await callApi('POST', url, props.userToken, requestBody);
            let tmpActions = [...actions];
            tmpActions.find(a => a === action).requestFormIsOpen = false;
            setActions(tmpActions);
            refreshClicked();
        } catch (err) {
            setError(err.message);
        }
    }

    function createNavBar() {
        if (!props.dataContext) return;
        if (!layout || (props.prmValues && !layout.allowNew)) return;
        let actionMenuItems = null;
        let actionForms = null;
        if (actions) {
            //  actionMenuItems = actions.filter((a) => a.requestFormUrl).map((a, i) =>
            //      <NavDropdown.Item key={i} onClick={(e) => actionClicked(e, a)}>{a.caption}</NavDropdown.Item>
            //  );
            actionMenuItems = actions.map((a, i) =>
                <NavDropdown.Item key={i} onClick={(e) => actionClicked(e, a)}>{a.caption}</NavDropdown.Item>
            );
            actionForms = actions.filter((a) => a.requestFormUrl).map((a, i) =>
                <CrudForm key={i}
                    isActionRequestForm={true}
                    action={a}
                    active={a.requestFormIsOpen}
                    layoutUrl={a.requestFormUrl}
                    layout={actionLayout}
                    objUnderEdit={() => props.dataContex.getObjUnderEdit(actionLayout?.queryObject)}
                    onHide={() => actionFormCancelled(a)}
                    onActionOk={actionFormOk}
                    userToken={props.userToken}
                    dataContext={props.dataContext}
                />
            );
        }
        let hasChanges = props.dataContext.hasChanges(layout.queryObject);
        return (
            <Navbar size="sm" className="justify-content-between bt-1 py-1" expand="md">
                <div className="container-fluid">
                    <Navbar.Brand key="brand">{layout.caption}</Navbar.Brand>
                    <Navbar.Toggle aria-controls="navbarScroll" />
                    <Navbar.Collapse id="navbarScroll">
                        <Nav navbarScroll>
                            <NavDropdown variant="light" title="">
                                {actionMenuItems}
                                {actionMenuItems && actionMenuItems.length ? <NavDropdown.Divider /> : null}
                                <NavDropdown.Item active={isDashboardActive} onClick={() => setIsDashboardActive(prev => !prev)} >{t('toolkit.dashboard', language)}</NavDropdown.Item>
                                <NavDropdown.Divider />
                                <FieldSelector objectName={layout.queryObject} objectCaption={layout.caption}
                                    layoutColumns={layout.columns} userToken={props.userToken}
                                    layoutId={layout.layoutId} instanceName={layout.instanceName}
                                    onSelectedFieldsChanged={selectedFieldsChanged} />
                                <ListQueryWizard
                                    objectName={layout.queryObject}
                                    objectCaption={layout.caption}
                                    queryText={layout.queryText}
                                    userToken={props.userToken}
                                    layoutId={layout.layoutId}
                                    instanceName={layout.instanceName}
                                    querySaved={selectedFieldsChanged}

                                    onQueryChange={() => {
                                        // console.log('Resetting parameter values after query change');
                                        setPrmControlValues({});
                                        setPrmValues({});
                                    }}

                                />
                                <NavDropdown.Divider />
                                <NavDropdown.Item onClick={changeListType}>{type === "Table" ? t('toolkit.grid', language) : t('toolkit.table', language)}</NavDropdown.Item>
                                <NavDropdown.Item onClick={excelExport} >{t('toolkit.exportExcel', language)}</NavDropdown.Item>
                            </NavDropdown>
                        </Nav>
                        <Form className="d-flex">
                            <div className="d-flex gap-2">
                                {createPrmControls()}
                            </div>
                            <Button className="top-buttonsCustom" size="sm" key="refresh" variant="" onClick={refreshClicked}><RefreshIcon sx={IconStyles} /></Button>
                            {(layout.allowNew && editGrid) ? <Button className="ms-1 top-buttonsCustom" size="sm" variant="" onClick={() => { props.dataContext.insertNewRow(layout.queryObject, true, props.parentSelectedRow); }}><InsertIcon sx={IconStyles} /></Button> : null}
                            {(layout.allowNew) ? <Button className="ms-1 top-buttonsCustom" size="sm" variant="" onClick={() => activateEditForm(-1)}><InsertOnFormIcon sx={IconStyles} /></Button> : null}
                            {(layout.editFormUrl && type === "Grid") ? <Button className="ms-1 top-buttonsCustom" size="sm" variant="" onClick={() => activateEditForm(null)} disabled={(activeRowIndex === -1)}><EditIcon sx={IconStyles} /></Button> : null}
                            {(layout.allowDelete && type === "Grid") ? <Button className="ms-1 top-buttonsCustom" size="sm" variant="" onClick={() => { props.dataContext.deleteRow(layout.queryObject, null, getSelectedRowIndex()) }} disabled={(activeRowIndex === -1)}><DeleteIcon sx={IconStyles} /></Button> : null}
                            <Button className="ms-1 top-buttonsCustom" size="sm" variant="" disabled={!hasChanges}
                                onClick={() => {
                                    props.dataContext.undo(layout.queryObject)
                                }
                                }><UndoIcon sx={IconStyles} /></Button>
                            <Button className="ms-1 top-buttonsCustom" size="sm" variant="" disabled={!hasChanges}
                                onClick={() => {
                                    props.dataContext.saveData(layout.queryObject, layout.columns, [layout.primaryKey, layout.additionalColumns])
                                }
                                }><SaveIcon sx={IconStyles} /></Button>
                            {toolbarActions.filter((a) => a.requestFormUrl).map((a, i) =>
                                <Button className="ms-1 top-buttonsCustom" size="sm" key={i} onClick={(e) => actionClicked(e, a)}>{a.caption}</Button>
                            )}
                            {props.onLayoutSettingsClicked ? <Button className="ms-1 top-buttonsCustom" size="sm" variant="" onClick={() => props.onLayoutSettingsClicked()}><SettingsIcon sx={IconStyles} /></Button> : props.onLayoutSettingsClicked}
                        </Form>
                    </Navbar.Collapse>
                </div>
                {actionForms}
            </Navbar>
        );
    }

    function createHeader() {
        if (!layout || !layout.columns) return;
        let headerCols = layout.columns.map((col, i) => {
            return (
                <th scope="col" key={i}>{col.caption}</th>
            );
        });
        if (props.printHandler) {
            headerCols = <><th scope="col" key="rhhp" className="controlCol"></th>{headerCols}</>;
        }
        if (layout.editFormUrl) {
            headerCols = <><th scope="col" key="rhhe" className="controlCol"></th>{headerCols}</>;
        }
        return headerCols;
    }

    function createCell(obj, col, rowIndex) {
        let colValue = getPropValue(obj, col.columnName);
        if (col.format === 'd') {
            let d = new Date(colValue);
            colValue = d.getDate().toString().padStart(2, '0') + "/" + (d.getMonth() + 1).toString().padStart(2, '0') + "/" + d.getFullYear();
        } else if (col.format === 't') {
            //let d = new Date(colValue);
            let d = parse(colValue, "HH:mm", new Date());
            colValue = d.getHours().toString().padStart(2, '0') + ":" + d.getMinutes().toString().padStart(2, '0');
        } else if (col.format === 'dt') {
            let d = new Date(colValue);
            colValue = d.getDate().toString().padStart(2, '0') + "/" + (d.getMonth() + 1).toString().padStart(2, '0') + "/" + d.getFullYear() + " " +
                d.getHours().toString().padStart(2, '0') + ":" + d.getMinutes().toString().padStart(2, '0');
        } else if (col.controlType === 'DropDownEnumList' && colValue && col.controlOptions) {
            let enm = col.controlOptions.find(enm => enm.value === colValue);
            if (enm && enm.text) colValue = enm.text;
        } else if (col.controlType === 'ImageUrl' && col.path === "relative") {
            colValue = serverUrl + "/" + colValue;
        }
        if (col.controlType === "CheckBox") {
            return (
                <td key={rowIndex}><input type="checkbox" className="checkbox" checked={colValue} readOnly /></td>
            );
        } else if (col.controlType === "ImageUrl") {
            return (
                <a target="_blank" rel="noopener noreferrer" href={colValue}><Image src={colValue} style={{ maxHeight: "50px" }} rounded fluid /></a>
            );
        } else {
            return (
                <td key={rowIndex}>
                    {colValue}
                </td>
            );
        }
    }

    function createRows() {
        if (!layout) return null;
        let data = props.dataContext.getLoadedData(layout.queryObject);
        if (!data) return null;
        return data.map((obj, i) => {
            let rowCols = layout.columns.map((col, i) => {
                return createCell(obj, col, i)
            });
            let editButton = null;
            let printButton = null;
            if (layout.editFormUrl) {
                editButton = <td key="rhde">
                    <Button size="sm" variant="" onClick={() => activateEditForm(i)}><EditIcon /></Button>
                </td>;
            }
            return (
                <tr key={i}>
                    {editButton}
                    {printButton}
                    {rowCols}
                </tr>
            );
        });
    }

    function createTable() {
        return (
            <table className="table table-sm table-striped">
                <thead><tr>{createHeader()}</tr></thead>
                <tbody>{createRows()}</tbody>
            </table>
        );
    }
    function isEdit(column) {
        if (!layout) return false;
        if (layout.allowUpdate === false) return false;
        if (column.noEdit === false) return true;
    }
    function controlType(col) {
        const dataType = col.dataType?.toLowerCase();
        const controlType = col.controlType?.toLowerCase();
        const format = col.format?.toLowerCase();

        if (controlType === 'checkbox') return 'boolean';
        if (dataType === 'double') return 'number';

        if (dataType === 'datetime') {
            if (format === 'd') return 'date';
            if (format === 'dt' || format === 't') return 'dateTime';
        }

        return 'string';
    }
    const getDefaultRenderEditCell = (col, options = {}) => {
        const colType = controlType(col);
        const { locale, decimals } = options;
        return (params) => {
            switch (colType) {
                case 'number':
                    return (
                        <TextField
                            type="number"
                            value={params.value ?? ''}
                            onChange={(e) =>
                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value: Round(parseFloat(e.target.value), decimals),
                                })
                            }
                            fullWidth
                        />
                    );
                case 'date':
                case 'dateTime':
                    return <GridDateEditCell {...params} locale={locale || 'el'} />;

                case 'boolean':
                    return (
                        <Checkbox
                            checked={!!params.value}
                            onChange={(e) =>
                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value: e.target.checked,
                                })
                            }
                        />
                    );

                case 'string':
                default:
                    return (
                        <TextField
                            value={params.value ?? ''}
                            onChange={(e) =>
                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value: e.target.value,
                                })
                            }
                            fullWidth
                        />
                    );
            }
        };
    };

    function createGrid() {
        if (!layout) return null;
        if (!props.dataContext) return null;
        let data = props.dataContext.getLoadedData(layout.queryObject);

        if (!data) return null;

        let gridData = data.map((r, i) => {
            let obj = {};
            layout.columns.forEach((c) => {
                const colType = controlType(c);
                let raw = getPropValue(r, c.columnName);
                const decimals = Number(c.decimals || 0);
                const fieldName = c.columnName.replaceAll(".", "_");
                switch (colType) {
                    case 'date':
                    case 'dateTime':
                        raw = raw ? new Date(raw) : null;
                        break;

                    case 'number':
                        raw = raw ? Round(parseFloat(raw), decimals) : null;
                        break;

                    default:
                        // default handling if needed
                        break;
                }
                /* 
                // Αν είναι ημερομηνία, τη μετατρέπουμε σε Date object
                if (controlType(c) === 'date' || controlType(c) === 'dateTime') {
                    raw = raw ? new Date(raw) : null;
                }
                */
                obj[fieldName] = raw;

                if (c.controlType === 'DropDownEnumList') {
                    let option = c.controlOptions.find(enm => enm.value === raw);
                    if (option) obj[fieldName] = option.text;
                }
            });
            obj["id"] = i;
            return obj;
        });

        let gridCols = layout.columns.map((c, i) => {
            //if (c.isHidden).....
            //if (c.format === "d").....
            if (c.columnName.toLowerCase().endsWith('imagepath')) {
                return {
                    field: c.columnName.replaceAll(".", "_"),
                    ...imageColumnType,
                    headerName: '',//c.caption,
                    flex: 30,
                    editable: false,
                }
            }
            if (c.controlType === 'DropDownEnumList') {
                return {
                    field: c.columnName.replaceAll(".", "_"),
                    headerName: c.caption,
                    flex: 30,
                    editable: false,
                }
            }


            const colType = controlType(c);
            const isDate = colType === 'date' || colType === 'dateTime';
            const decimals = Number(c.decimals || 0);
            const renderEditCell = getDefaultRenderEditCell(c, { language, decimals });
            return {
                field: c.columnName.replaceAll(".", "_"),
                headerName: c.caption,
                flex: (c.dataLength < 20) ? 20 : (c.dataLength > 40) ? 40 : c.dataLength,
                editable: (isEdit(c) && editGrid),

                //type: c.controlType === 'CheckBox' ? 'boolean' : (c.dataType === 'Double' ? 'number' : 'string'),
                type: colType,
                // renderEditCell: isDate ? (params) => <GridDateEditCell {...params} locale="el" /> : undefined,

                renderEditCell: renderEditCell,

                valueFormatter: colType === 'number'
                    ? (params) => {
                        const val = Number(params);
                        return isNaN(val) ? '' : val.toFixed(c.decimals || 2);
                    }
                    : isDate
                        ? (params) => {
                            const value = params;
                            const date = value instanceof Date ? value : new Date(value);
                            return date && !isNaN(date.getTime())
                                ? format(date, 'dd/MM/yyyy HH:mm', { locale: localeMap[language] })
                                : '';
                        }
                        : undefined,

                preProcessEditCellProps: (params) => {
                    const hasError = false;
                    if (params.hasChanged) {
                        props.dataContext.onColumnChanged(layout.queryObject, c.columnName, params.props.value);
                    }
                    return { ...params.props, error: hasError };
                },
                minWidth: 150,
                cellClassName: 'grid-cell'
            };
        });

        return (
            <div style={{ height: 'calc(100% - 53px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <DataGrid
                    sx={theme => ({
                        backgroundColor: app.theme === 'dark' ? '#212529' : '#fff',
                        color: app.theme === 'dark' ? '#fff' : '#000',
                        // '& .MuiDataGrid-iconSeparator': {
                        //     display: 'none',
                        // },
                        '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                            borderRight: `1px solid ${app.theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
                                }`,
                        },
                        '& .MuiDataGrid-columnsContainer, & .MuiDataGrid-cell': {
                            borderBottom: `1px solid ${app.theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
                                }`,
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: 'var(--color-capacity-background) !important',
                        },
                        '& .MuiDataGrid-row:focus-within': {
                            backgroundColor: 'var(--color-capacity-background) !important',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: '2px solid var(--color-main-background) !important',
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: 'var(--color-capacity-full-background) !important',
                        },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                            outline: '1px solid var(--color-main-background) !important',
                        },
                        '& .MuiDataGrid-overlay': {
                            fontWeight: 'bold',
                        },
                        '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]': {
                            backgroundColor: app.theme === 'dark' ? '#212529 !important' : '#fff !important',
                            color: app.theme === 'dark' ? '#fff !important' : '#000 !important',
                        },
                        '& svg': {
                            width: '1em',
                            height: '1em',
                            fill: 'currentColor',
                            fontSize: 'inherit'
                        }
                    })}
                    style={{ height: isDashboardActive ? '30%' : '100%', width: '99%' }}
                    editMode='row'

                    apiRef={gridApiRef}
                    rows={gridData}
                    columns={gridCols}
                    isCellEditable={(params) =>
                        props.dataContext.getRowsRestrictions(layout.queryObject, params.row.id)}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 100 } },
                    }}
                    pageSizeOptions={[100]}
                    getRowHeight={({ id, densityFactor }) => {
                        if (!gridData || !gridData[id]) return null;
                        let thereIsImage = false;
                        for (let key in gridData[id]) {
                            if (key.toLowerCase().endsWith('imagepath') && gridData[id][key]) {
                                thereIsImage = true;
                                break;
                            }
                        }
                        if (thereIsImage) return 100 * densityFactor;
                        else return null;
                    }}
                    onRowClick={(params, event, details) => {
                        if (data && params && params.row) {
                            rowSelectedCallBack(data[params.row.id]);
                            setActiveRowIndex(params.row.id)
                        }
                    }}
                    onRowEditStart={(params, event) => {
                        props.dataContext.beginEdit(layout.queryObject, data[params.id]);
                    }}
                    onRowEditStop={(params, event) => {
                        props.dataContext.endEdit(layout.queryObject);
                    }}
                    localeText={{
                        noRowsLabel: t("common.noData", language),
                        footerTotalRows: t("common.totalRows", language),
                        footerRowSelected: (count) => {
                            if (count === 1) {
                                const template = t("common.rowSelected", language)
                                return template.replace("{{count}}", count);
                            }
                            const template = t("common.rowsSelected", language);
                            return template.replace("{{count}}", count);
                        },

                        MuiTablePagination: {
                            labelRowsPerPage: t("common.rowsPerPage", language),
                            labelDisplayedRows: ({ from, to, count }) =>
                                t("common.displayedRows", language, {
                                    from,
                                    to,
                                    count: count !== -1 ? count : t("common.moreThan", language, { to }),
                                }),
                        },
                    }}
                    autosizeOptions={{
                        columns: ['all'],
                        includeOutliers: true,
                        includeHeaders: false,
                    }}
                    processRowUpdate={(updatedRow, oldRow) => {
                        return updatedRow;
                    }}
                    onProcessRowUpdateError={(error) => {
                        setError("Σφάλμα κατά την επεξεργασία γραμμής:", error);
                    }}
                />
                {isDashboardActive ? <div className='grid-dashboard container' style={{ height: '70%', width: '100%', overflowY: 'scroll' }}><GridDashboard columns={layout.columns} data={data} /></div> : null}
            </div>
        );
    }

    function createList() {
        if (type === "Table") {
            return createTable();
        } else {
            return createGrid();
        }
    }

    function createForm() {
        if (!props.dataContext || !layout) return null;
        return <CrudForm
            dataContext={props.dataContext}
            userToken={props.userToken}
            layout={props.dataContext.getFormLayout(layout.queryObject)}
            instanceName={layout.instanceName}
            objUnderEdit={props.dataContext.getObjUnderEdit(layout.queryObject)}
            allowDelete={layout ? layout.allowDelete : null}
            allowUpdate={layout ? layout.allowUpdate : null}
            onHide={async () => {
                await props.dataContext.endEdit(layout.queryObject);
                setEditFormActive(false);
            }}
            active={editFormActive} />;
    }

    return (
        <>
            {createForm()}
            <ErrorAlert message={error} setMessage={setError} language={language}/>
            {createNavBar()}
            {createList()}
        </>
    );

}

export default CrudList;